import React, { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Badge from "@mui/material/Badge";
import Tooltip from "@mui/material/Tooltip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import SearchIcon from "@mui/icons-material/Search";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AddIcon from "@mui/icons-material/Add";
import AddLocationAltIcon from "@mui/icons-material/AddLocationAlt";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { adminNodeListCameras } from "../../../api/adminNodeCameras.ts";
import {
  createPatrolDrawing,
  deletePatrolDrawing,
  fetchPatrolDrawings,
  fetchPatrolSectionCameras,
  savePatrolSectionCamerasBulk,
  updatePatrolDrawing,
} from "../../../api/patrol.ts";
import { AdministrationSectionHeader } from "../AdministrationSectionHeader.tsx";
import { useModuleCrudPermissions } from "../../../hooks/useModuleCrudPermissions.js";
import { ViewToggle } from "../../../components/aiDetectionSections/ViewToggle.tsx";
import { GLOBAL_TOOLTIP_PROPS } from "../../../config/tooltips.config.ts";
import AddPatrolDrawingDialog from "./AddPatrolDrawingDialog.jsx";
import EditPatrolDrawingDialog from "./EditPatrolDrawingDialog.jsx";
import AddDrawingCameraLocationDialog from "./AddDrawingCameraLocationDialog.jsx";
import DrawingActionsMenu from "./DrawingActionsMenu.jsx";
import DeletePatrolDrawingConfirmDialog from "./DeletePatrolDrawingConfirmDialog.jsx";
import { PatrolSettingsEmptyState } from "./PatrolSettingsEmptyState.jsx";
import PatrolDrawingMediaSurface from "./PatrolDrawingMediaSurface.jsx";
import CircularProgress from "@mui/material/CircularProgress";
import { getFileTypeFromFile } from "./patrolDrawingMedia.js";
import {
  countPlacementLocations,
  enrichPlacementsWithCameraLabels,
  getDrawingCameraPlacements,
  markersForDisplay,
  patrolCameraOptionFromAdminRow,
} from "./patrolSectionCameraLocation.js";

function mergeDrawingPlacements(drawing, placements, cameras) {
  const enriched = enrichPlacementsWithCameraLabels(placements, cameras);
  return {
    ...drawing,
    cameraPlacements: enriched,
    markers: markersForDisplay(enriched, cameras),
  };
}

async function loadDrawingWithPlacements(drawing, cameras) {
  try {
    const placements = await fetchPatrolSectionCameras(drawing.id);
    return mergeDrawingPlacements(drawing, placements, cameras);
  } catch (err) {
    console.warn(
      `Patrol section cameras unavailable for drawing ${drawing.id}`,
      err,
    );
    return {
      ...drawing,
      markers: markersForDisplay(getDrawingCameraPlacements(drawing), cameras),
    };
  }
}

const CARD_WIDTH = 200;
const PREVIEW_HEIGHT = 168;

const toolbarFieldSx = {
  width: { xs: "100%", sm: "240px" },
  minWidth: { xs: 0, sm: 240 },
  flex: { xs: "1 1 100%", sm: "0 0 auto" },
  "& .MuiOutlinedInput-root": {
    height: 36,
    fontFamily: "Roboto, sans-serif",
    fontSize: "14px",
    bgcolor: "#fff",
    borderRadius: "10px",
    "& fieldset": { borderColor: "#E5E7EB" },
  },
};

const filterSelectSx = {
  height: 36,
  minWidth: { xs: 100, sm: 120 },
  fontFamily: "Roboto, sans-serif",
  fontSize: "14px",
  borderRadius: "10px",
  bgcolor: "#fff",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5E7EB" },
};

function FloorPlanPreview({ drawing, cameras }) {
  const markers = useMemo(
    () =>
      markersForDisplay(getDrawingCameraPlacements(drawing), cameras),
    [drawing, cameras],
  );
  return (
    <PatrolDrawingMediaSurface
      drawing={drawing}
      markers={markers}
      minHeight={PREVIEW_HEIGHT}
      compact
    />
  );
}

function getDrawingLocationCount(drawing) {
  return countPlacementLocations(getDrawingCameraPlacements(drawing));
}

function EmptyItemsState({ message }) {
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 280,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        color: "#9CA3AF",
      }}
    >
      <Inventory2OutlinedIcon sx={{ fontSize: 48, opacity: 0.5 }} />
      <Typography sx={{ fontFamily: "Roboto, sans-serif", fontSize: 14 }}>
        {message}
      </Typography>
    </Box>
  );
}

function PatrolSettingsToolbar({
  searchQuery,
  onSearchChange,
  searchPlaceholder,
  showFavoritesOnly,
  onToggleFavorites,
  favoritesCount,
  viewMode,
  onViewModeChange,
  dateFilter,
  onDateFilterChange,
  addButtonLabel,
  onAdd,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1,
        alignItems: "center",
        pb: 2,
      }}
    >
      <TextField
        size="small"
        placeholder={searchPlaceholder}
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        sx={toolbarFieldSx}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 20, color: "#9E9E9E" }} />
            </InputAdornment>
          ),
        }}
      />

      <Tooltip
        title={showFavoritesOnly ? "Show all" : "Show favorites only"}
        {...GLOBAL_TOOLTIP_PROPS}
      >
        <IconButton
          size="small"
          onClick={onToggleFavorites}
          sx={{
            border: "1px solid #E5E7EB",
            borderRadius: "10px",
            height: 36,
            width: 36,
            bgcolor: showFavoritesOnly ? "#FFE0D0" : "#fff",
          }}
        >
          <Badge
            badgeContent={favoritesCount}
            color="primary"
            sx={{
              "& .MuiBadge-badge": {
                fontSize: "10px",
                height: 16,
                minWidth: 16,
                bgcolor: "#2932E5",
              },
            }}
          >
            {showFavoritesOnly ? (
              <StarIcon fontSize="small" sx={{ color: "#FF8555" }} />
            ) : (
              <StarBorderIcon fontSize="small" sx={{ color: "#9E9E9E" }} />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      <ViewToggle view={viewMode} onChange={onViewModeChange} />

      <FormControl size="small" sx={{ minWidth: { xs: 100, sm: 120 } }}>
        <Select
          value={dateFilter}
          onChange={(e) => onDateFilterChange(e.target.value)}
          displayEmpty
          sx={{
            ...filterSelectSx,
            bgcolor: dateFilter !== "all" ? "#FFE0D0" : "#fff",
          }}
          renderValue={(v) => (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <CalendarTodayIcon sx={{ fontSize: 16, color: "#6B7280" }} />
              <span>
                {v === "all"
                  ? "All Dates"
                  : v === "today"
                    ? "Today"
                    : "Last 7 Days"}
              </span>
            </Box>
          )}
        >
          <MenuItem value="all">All Dates</MenuItem>
          <MenuItem value="today">Today</MenuItem>
          <MenuItem value="week">Last 7 Days</MenuItem>
        </Select>
      </FormControl>

      <Box sx={{ flex: 1, minWidth: 8 }} />

      {onAdd ? (
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={onAdd}
        sx={{
          textTransform: "none",
          fontFamily: "Roboto, sans-serif",
          fontSize: 14,
          fontWeight: 500,
          bgcolor: "#2932E5",
          borderRadius: "10px",
          height: 36,
          px: 2,
          boxShadow: "none",
          "&:hover": { bgcolor: "#1E25B8", boxShadow: "none" },
        }}
      >
        {addButtonLabel}
      </Button>
      ) : null}
    </Box>
  );
}

function DrawingRowActions({
  drawing,
  onEdit,
  onDelete,
  onAddCameraLocation,
  canUpdate,
  canDelete,
  canCreateLocation,
}) {
  const showActions = canUpdate || canDelete || canCreateLocation;
  if (!showActions) return null;
  return (
    <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end", alignItems: "center", flexWrap: "wrap" }}>
      {canCreateLocation ? (
      <Tooltip title="Add camera location on drawing" {...GLOBAL_TOOLTIP_PROPS}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddLocationAltIcon sx={{ fontSize: 18 }} />}
          onClick={() => onAddCameraLocation(drawing)}
          sx={{
            textTransform: "none",
            fontSize: 13,
            borderColor: "#E5E7EB",
            color: "#2932E5",
            borderRadius: "8px",
            whiteSpace: "nowrap",
          }}
        >
          Add location
        </Button>
      </Tooltip>
      ) : null}
      {canUpdate || canDelete ? (
      <DrawingActionsMenu
        drawing={drawing}
        onEdit={canUpdate ? onEdit : undefined}
        onDelete={canDelete ? onDelete : undefined}
      />
      ) : null}
    </Box>
  );
}

function DrawingTabPanel({
  viewMode,
  searchQuery,
  showFavoritesOnly,
  drawings,
  cameras,
  onAddDrawing,
  onAddCameraLocation,
  onEditDrawing,
  onDeleteDrawing,
  canCreate,
  canUpdate,
  canDelete,
}) {
  const filtered = useMemo(() => {
    let list = drawings;
    if (showFavoritesOnly) list = list.filter((d) => d.isFavorite);
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter((d) => d.title.toLowerCase().includes(q));
    }
    return list;
  }, [drawings, searchQuery, showFavoritesOnly]);

  if (viewMode === "list") {
    if (filtered.length === 0) {
      return <EmptyItemsState message="No drawings yet." />;
    }
    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Markers</TableCell>
              <TableCell>Favorite</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((d) => (
              <TableRow key={d.id} hover>
                <TableCell>{d.title}</TableCell>
                <TableCell>{getDrawingLocationCount(d)}</TableCell>
                <TableCell>{d.isFavorite ? "Yes" : "—"}</TableCell>
                <TableCell align="right">
                  <DrawingRowActions
                    drawing={d}
                    onEdit={onEditDrawing}
                    onDelete={onDeleteDrawing}
                    onAddCameraLocation={onAddCameraLocation}
                    canCreateLocation={canUpdate}
                    canUpdate={canUpdate}
                    canDelete={canDelete}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: 2,
        overflowX: "auto",
        pb: 1,
        WebkitOverflowScrolling: "touch",
      }}
    >
      {canCreate ? (
      <Paper
        elevation={0}
        variant="outlined"
        onClick={onAddDrawing}
        sx={{
          flex: "0 0 auto",
          width: CARD_WIDTH,
          borderRadius: 1,
          borderColor: "#D1D5DB",
          overflow: "hidden",
          cursor: "pointer",
          "&:hover": { borderColor: "#2932E5" },
        }}
      >
        <Box
          sx={{
            px: 1.25,
            py: 0.75,
            bgcolor: "#E8EAEF",
            borderBottom: "1px solid #D1D5DB",
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
            New drawing
          </Typography>
        </Box>
        <Box
          sx={{
            height: PREVIEW_HEIGHT,
            bgcolor: "#111827",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AddIcon sx={{ fontSize: 40, color: "#fff" }} />
        </Box>
      </Paper>
      ) : null}
      {filtered.map((site) => (
        <Paper
          key={site.id}
          elevation={0}
          variant="outlined"
          sx={{
            flex: "0 0 auto",
            width: CARD_WIDTH,
            borderRadius: 1,
            borderColor: "#D1D5DB",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 1.25,
              py: 0.75,
              bgcolor: "#E8EAEF",
              borderBottom: "1px solid #D1D5DB",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
              }}
            >
              {site.title}
            </Typography>
            {canUpdate ? (
            <Tooltip title="Add camera location" {...GLOBAL_TOOLTIP_PROPS}>
              <IconButton
                size="small"
                onClick={() => onAddCameraLocation(site)}
                aria-label="Add camera location"
                sx={{ color: "#2932E5" }}
              >
                <AddLocationAltIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            ) : null}
            <DrawingActionsMenu
              drawing={site}
              onEdit={canUpdate ? onEditDrawing : undefined}
              onDelete={canDelete ? onDeleteDrawing : undefined}
              iconSize={18}
            />
          </Box>
          <FloorPlanPreview drawing={site} cameras={cameras} />
        </Paper>
      ))}
    </Box>
  );
}

/** Administration → Patrol setting: manage site drawings and camera placements. */
export default function PatrolSettingsAdminPage() {
  const { create: canCreate, update: canUpdate, delete: canDelete } =
    useModuleCrudPermissions("zones");
  const [searchQuery, setSearchQuery] = useState("");
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [viewMode, setViewMode] = useState("list");
  const [dateFilter, setDateFilter] = useState("all");
  const [drawings, setDrawings] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [camerasLoading, setCamerasLoading] = useState(true);
  const [drawingsLoading, setDrawingsLoading] = useState(false);
  const [addDrawingOpen, setAddDrawingOpen] = useState(false);
  const [editDrawing, setEditDrawing] = useState(null);
  const [deleteDrawingTarget, setDeleteDrawingTarget] = useState(null);
  const [deleteDrawingBusy, setDeleteDrawingBusy] = useState(false);
  const [deleteDrawingError, setDeleteDrawingError] = useState(null);
  const [cameraLocationDrawing, setCameraLocationDrawing] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setDrawingsLoading(true);
      setCamerasLoading(true);
      try {
        const [cameraRows, list] = await Promise.all([
          adminNodeListCameras().catch((err) => {
            console.warn("Camera list API unavailable for patrol placements.", err);
            if (!cancelled) {
              toast.error(
                err?.message ||
                  "Could not load cameras from Camera management.",
              );
            }
            return [];
          }),
          fetchPatrolDrawings().catch((err) => {
            console.warn("Patrol drawings API unavailable.", err);
            return [];
          }),
        ]);

        const cameraOptions = cameraRows
          .map(patrolCameraOptionFromAdminRow)
          .filter(Boolean);

        if (!cancelled) {
          setCameras(cameraOptions);
        }

        if (!cancelled) {
          if (list.length > 0) {
            const enriched = await Promise.all(
              list.map((d) => loadDrawingWithPlacements(d, cameraOptions)),
            );
            if (!cancelled) setDrawings(enriched);
          } else {
            setDrawings([]);
          }
        }
      } finally {
        if (!cancelled) {
          setDrawingsLoading(false);
          setCamerasLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const drawingTitles = useMemo(
    () => drawings.map((d) => d.title),
    [drawings],
  );

  const openAddDrawingDialog = useCallback(() => {
    setAddDrawingOpen(true);
  }, []);

  const reloadCameras = useCallback(async () => {
    setCamerasLoading(true);
    try {
      const rows = await adminNodeListCameras();
      setCameras(
        rows.map(patrolCameraOptionFromAdminRow).filter(Boolean),
      );
    } catch (err) {
      console.warn("Failed to refresh camera list for patrol.", err);
      toast.error(
        err?.message || "Could not load cameras from Camera management.",
      );
      setCameras([]);
    } finally {
      setCamerasLoading(false);
    }
  }, []);

  const openCameraLocationDialog = useCallback(
    (drawing) => {
      setCameraLocationDrawing(drawing);
      void reloadCameras();
    },
    [reloadCameras],
  );

  const openEditDrawingDialog = useCallback((drawing) => {
    setEditDrawing(drawing);
  }, []);

  const requestDeleteDrawing = useCallback((drawing) => {
    setDeleteDrawingError(null);
    setDeleteDrawingTarget(drawing);
  }, []);

  const closeDeleteDrawingDialog = useCallback(() => {
    if (deleteDrawingBusy) return;
    setDeleteDrawingTarget(null);
    setDeleteDrawingError(null);
  }, [deleteDrawingBusy]);

  const confirmDeleteDrawing = useCallback(async () => {
    const drawing = deleteDrawingTarget;
    if (!drawing || deleteDrawingBusy) return;

    setDeleteDrawingBusy(true);
    setDeleteDrawingError(null);
    try {
      await deletePatrolDrawing(drawing.id);
      setDrawings((prev) => prev.filter((d) => d.id !== drawing.id));
      if (cameraLocationDrawing?.id === drawing.id) {
        setCameraLocationDrawing(null);
      }
      if (editDrawing?.id === drawing.id) {
        setEditDrawing(null);
      }
      setDeleteDrawingTarget(null);
      toast.success("Drawing deleted.");
    } catch (err) {
      const message = err?.message || "Failed to delete drawing.";
      setDeleteDrawingError(message);
      toast.error(message);
    } finally {
      setDeleteDrawingBusy(false);
    }
  }, [
    deleteDrawingTarget,
    deleteDrawingBusy,
    cameraLocationDrawing?.id,
    editDrawing?.id,
  ]);

  const handleUpdateDrawing = useCallback(
    async (drawingId, payload) => {
      const updated = await updatePatrolDrawing(drawingId, {
        name: payload.title,
        description: payload.description,
        isFavorite: payload.isFavorite,
        file: payload.file,
      });

      setDrawings((prev) =>
        prev.map((d) => {
          if (d.id !== drawingId) return d;
          const fileType =
            updated.fileType ||
            (payload.file ? getFileTypeFromFile(payload.file) : d.fileType);
          const fileUrl = updated.fileUrl || d.fileUrl;
          return {
            ...d,
            ...updated,
            title: updated.title || payload.title,
            fileUrl,
            fileType,
            fileName: updated.fileName || d.fileName,
            imagePreview:
              fileType === "image"
                ? updated.imagePreview || fileUrl
                : null,
            cameraPlacements: d.cameraPlacements,
            markers: d.markers,
          };
        }),
      );
    },
    [],
  );

  const cameraLocationDrawingResolved = useMemo(() => {
    if (!cameraLocationDrawing) return null;
    return (
      drawings.find((d) => d.id === cameraLocationDrawing.id) ||
      cameraLocationDrawing
    );
  }, [cameraLocationDrawing, drawings]);

  const handleSaveCameraLocations = useCallback(
    async (drawingId, cameraPlacements) => {
      await savePatrolSectionCamerasBulk(drawingId, cameraPlacements);
      setDrawings((prev) =>
        prev.map((d) =>
          d.id === drawingId
            ? mergeDrawingPlacements(d, cameraPlacements, cameras)
            : d,
        ),
      );
    },
    [cameras],
  );

  const handleSaveDrawing = useCallback(async (payload) => {
    const created = await createPatrolDrawing({
      file: payload.file,
      name: payload.title,
      description: payload.description,
      isFavorite: payload.isFavorite ?? false,
    });

    const fileType =
      created.fileType || getFileTypeFromFile(payload.file);
    const fileUrl =
      created.fileUrl ||
      payload.fileUrl ||
      URL.createObjectURL(payload.file);

    const drawing = {
      ...created,
      title: created.title || payload.title,
      fileUrl,
      fileType,
      fileName: created.fileName || payload.file?.name || "",
      imagePreview:
        fileType === "image"
          ? created.imagePreview || payload.previewUrl || fileUrl
          : null,
      cameraPlacements: created.cameraPlacements || [],
      markers: [],
    };
    drawing.markers = markersForDisplay(
      getDrawingCameraPlacements(drawing),
      cameras,
    );

    setDrawings((prev) => [...prev, drawing]);
  }, [cameras]);

  const favoritesCount = useMemo(
    () => drawings.filter((d) => d.isFavorite).length,
    [drawings],
  );

  const hasDrawings = drawings.length > 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        minHeight: 0,
        flex: 1,
        width: "100%",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
      >
        <Paper
          elevation={1}
          sx={{
            minHeight: 480,
            flex: 1,
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "20px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <AdministrationSectionHeader
            title="Patrol setting"
            infoTooltip="Manage site drawings for Digital Patrol. Upload floor plans, place cameras on drawings, and search or filter your list."
          />

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              p: { xs: 1, sm: 2 },
              display: "flex",
              flexDirection: "column",
            }}
          >
            {drawingsLoading ? (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 280,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={40} sx={{ color: "#2932E5" }} />
              </Box>
            ) : !hasDrawings ? (
              <PatrolSettingsEmptyState onAddDrawing={canCreate ? openAddDrawingDialog : undefined} />
            ) : (
              <>
                <PatrolSettingsToolbar
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  searchPlaceholder="Search drawings..."
                  showFavoritesOnly={showFavoritesOnly}
                  onToggleFavorites={() => setShowFavoritesOnly((v) => !v)}
                  favoritesCount={favoritesCount}
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  dateFilter={dateFilter}
                  onDateFilterChange={setDateFilter}
                  addButtonLabel="New Drawing"
                  onAdd={canCreate ? openAddDrawingDialog : undefined}
                />

                <DrawingTabPanel
                  viewMode={viewMode}
                  searchQuery={searchQuery}
                  showFavoritesOnly={showFavoritesOnly}
                  drawings={drawings}
                  cameras={cameras}
                  onAddDrawing={openAddDrawingDialog}
                  onAddCameraLocation={openCameraLocationDialog}
                  onEditDrawing={openEditDrawingDialog}
                  onDeleteDrawing={requestDeleteDrawing}
                  canCreate={canCreate}
                  canUpdate={canUpdate}
                  canDelete={canDelete}
                />
              </>
            )}
          </Box>
        </Paper>
      </motion.div>

      <AddPatrolDrawingDialog
        open={addDrawingOpen}
        onClose={() => setAddDrawingOpen(false)}
        onSave={handleSaveDrawing}
        existingTitles={drawingTitles}
      />

      <EditPatrolDrawingDialog
        open={Boolean(editDrawing)}
        drawing={editDrawing}
        onClose={() => setEditDrawing(null)}
        onSave={handleUpdateDrawing}
        existingTitles={drawingTitles}
      />

      <DeletePatrolDrawingConfirmDialog
        open={Boolean(deleteDrawingTarget)}
        drawingTitle={deleteDrawingTarget?.title}
        loading={deleteDrawingBusy}
        error={deleteDrawingError}
        onClose={closeDeleteDrawingDialog}
        onConfirm={confirmDeleteDrawing}
      />

      <AddDrawingCameraLocationDialog
        open={Boolean(cameraLocationDrawing)}
        drawing={cameraLocationDrawingResolved}
        cameras={cameras}
        camerasLoading={camerasLoading}
        onClose={() => setCameraLocationDrawing(null)}
        onSave={handleSaveCameraLocations}
      />
    </Box>
  );
}
